import { useState } from 'react';
import {
  Alert,
  Button,
  ButtonLink,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
  TextField,
} from '@backstage/ui';
import { useApi, useTranslationRef } from '@backstage/frontend-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { relativeTime } from '../home/relativeTime';
import { confirmationText } from './environmentPower';
import type {
  AppTypeDescriptor,
  QuantityPair,
  VolumeConfigSummary,
} from '@internal/plugin-waadoo-config-common';
import type { KeptSummaryProps } from '../appModules/appTypeModule';
import { useKeptSummary } from '../appModules/useAppTypeModules';
import {
  useCurrentAppDescriptor,
  useCurrentAppType,
} from '../shared/AppTypeContext';
import {
  launchPromotion,
  promotionNeedsConfirmation,
  type EnvironmentConfigSummary,
  type PromotionPair,
  type PromotionResponse,
} from './promotion';
import { projectEnvironmentsTranslationRef } from './translation';

const PromotionIcon = waadooIcons.promotion;
const CommitIcon = waadooIcons.revision;

/** `100m / 1600m`, either side alone when the other is unknown. */
function range(value?: QuantityPair): string {
  return [value?.request, value?.limit].filter(Boolean).join(' / ');
}

/**
 * The lines of « Ce qui ne change pas », one per fact: the list the core's
 * and the type modules' summaries (`AppTypeModule.promotion.KeptSummary`)
 * render.
 */
export function KeptLines(props: { lines: readonly string[] }) {
  return (
    <ul>
      {props.lines.map(line => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

const DATA_KEYS = {
  both: 'promote.keptData',
  database: 'promote.keptDataDatabase',
  files: 'promote.keptDataFiles',
  unknown: 'promote.keptDataGeneric',
} as const;

/**
 * What a type keeps as data: a database (a CloudNativePG or MariaDB
 * component), files (a field of role `volume`), both, none; `unknown`
 * without its descriptor.
 */
function keptData(
  descriptor: AppTypeDescriptor | undefined,
): keyof typeof DATA_KEYS | 'none' {
  if (!descriptor) {
    return 'unknown';
  }
  const database =
    descriptor.databaseComponent !== undefined ||
    descriptor.mariadbComponent !== undefined;
  const files = [...descriptor.componentRoles.values()].some(
    roles => roles.volumes.length > 0,
  );
  if (database && files) return 'both';
  if (database) return 'database';
  return files ? 'files' : 'none';
}

/**
 * « Ce qui ne change pas » (mockup promo-D) for any type (genericity plan,
 * step 6): the target's configuration as its appenv `values.yaml` says it
 * (`summarizeEnvironmentConfig` of the engine, by the backend), one line per
 * fact read — each `app` component's capacity and pods (the main one
 * first), the volumes, the other components, the other settings —; the
 * generic sentence when nothing could be read; then what the type keeps as
 * data, by its descriptor (`useCurrentAppDescriptor`). A type's module may
 * render its own (`KeptSummary`: Odoo's words).
 */
function KeptList(props: KeptSummaryProps) {
  const { target, config } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const descriptor = useCurrentAppDescriptor();
  const lines: string[] = [];
  const components = config?.capacity ?? [];
  const apps = [
    ...components.filter(item => item.kind === 'app' && item.main),
    ...components.filter(item => item.kind === 'app' && !item.main),
  ];
  for (const app of apps) {
    if (app.enabled === false) {
      lines.push(t('promote.keptDisabled', { component: app.label }));
      continue;
    }
    if (app.cpu || app.memory) {
      lines.push(
        t('promote.keptCapacity', {
          component: app.label,
          cpu: range(app.cpu) || '?',
          memory: range(app.memory) || '?',
        }),
      );
    }
    if (app.autoscaling?.enabled) {
      const bounds = {
        min: String(app.autoscaling.min ?? '?'),
        max: String(app.autoscaling.max ?? '?'),
      };
      lines.push(
        app.main
          ? t('promote.keptHpa', bounds)
          : t('promote.keptComponentHpa', { component: app.label, ...bounds }),
      );
    } else if (app.pods !== undefined) {
      lines.push(
        t('promote.keptPods', { component: app.label, count: app.pods }),
      );
    } else if (app.autoscaling) {
      lines.push(t('promote.keptNoAutoscaling', { component: app.label }));
    }
  }
  const volumes = config?.volumes ?? [];
  if (volumes.length > 0) {
    const mainVolumes = volumes.filter(
      volume => volume.role === 'volume' && volume.main,
    );
    const databases = volumes.filter(volume => volume.role === 'databaseSize');
    const labelOf = (volume: VolumeConfigSummary): string => {
      if (mainVolumes.length === 1 && volume === mainVolumes[0]) {
        return t('promote.volumeFiles');
      }
      if (databases.length === 1 && volume === databases[0]) {
        return t('promote.volumeDatabase');
      }
      return volume.label;
    };
    lines.push(
      t('promote.keptVolumes', {
        volumes: volumes
          .map(volume =>
            t('promote.keptVolume', {
              label: labelOf(volume),
              size: volume.size,
            }),
          )
          .join(', '),
      }),
    );
  }
  for (const other of components.filter(item => item.kind !== 'app')) {
    if (other.enabled === false) {
      // Said by nothing: a component switched off keeps nothing.
      continue;
    }
    if (other.kind === 'cnpg' && other.pods !== undefined) {
      lines.push(
        t('promote.keptDatabaseInstances', { instances: String(other.pods) }),
      );
    } else if (other.enabled) {
      lines.push(t('promote.keptEnabled', { component: other.label }));
    }
  }
  for (const setting of config?.settings ?? []) {
    let value = String(setting.value);
    if (typeof setting.value === 'boolean') {
      value = setting.value ? t('promote.valueYes') : t('promote.valueNo');
    }
    lines.push(t('promote.keptSetting', { label: setting.label, value }));
  }
  if (lines.length === 0) {
    lines.push(t('promote.keptGeneric', { target }));
  }
  const data = keptData(descriptor);
  if (data !== 'none') {
    lines.push(t(DATA_KEYS[data], { target }));
  }
  return <KeptLines lines={lines} />;
}

/**
 * « Promouvoir <source> → <cible> » (mockup promo-D, 2026-10-15): the commits
 * arriving on the target, what changes there (the code, the image, the
 * common settings) and what does not (its configuration, its data), how the
 * task goes, for the production `<project>/prod` to type. « Voir la merge
 * request » only when one is already open between the two branches — before
 * the task opens it, there is none to show. Launches `templateRef`; the
 * caller mounts it only while open and navigates once the task is created.
 *
 * No « wait for the merge request's pipeline » as in the mockup: the
 * project's CI has no merge request pipeline, the task merges at once.
 *
 * A `standard` project (`profile` of the route, point 8) has no image of its
 * own: its source must be deployed at its head, and the target is
 * redeployed at the merged revision (backend `promote.ts`) — no image line,
 * and its own « how ».
 */
export function PromoteDialog(props: {
  project: string;
  pair: PromotionPair;
  /**
   * The project's promotion template (`useProjectTemplates`); undefined
   * while its type designates none: « Promouvoir » stays disabled.
   */
  templateRef: string | undefined;
  /** The target's configuration, when the route could read it. */
  config?: EnvironmentConfigSummary;
  /** The target is asleep: it will be deployed at its next wake. */
  targetAsleep: boolean;
  /** The project's profile, as the promotion route says it. */
  profile: PromotionResponse['profile'];
  onClose: () => void;
  onLaunched: (taskId: string) => void;
}) {
  const {
    project,
    pair,
    templateRef,
    config,
    targetAsleep,
    profile,
    onClose,
    onLaunched,
  } = props;
  const standard = profile === 'standard';
  const { source, target } = pair;
  // The type module's « Ce qui ne change pas » (Odoo's), else the core's.
  const Kept = useKeptSummary(useCurrentAppType()) ?? KeptList;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string>();
  const production = promotionNeedsConfirmation(target);
  const expected = confirmationText(project, target);
  const confirmed = !production || typed === expected;
  const locale = t('format.locale');
  const now = Date.now();
  const hidden = pair.ahead - pair.commits.length;

  const howText = (): string => {
    if (standard) {
      return targetAsleep
        ? t('promote.howSleepingStandard', { source, target })
        : t('promote.howStandard', { source, target });
    }
    return targetAsleep
      ? t('promote.howSleeping', { source, target })
      : t('promote.how', { source, target });
  };

  const submit = async () => {
    if (templateRef === undefined) return;
    setPending(true);
    setFailure(undefined);
    try {
      onLaunched(
        await launchPromotion(scaffolderApi, {
          templateRef,
          project,
          source,
          target,
          ...(production ? { confirmation: typed } : {}),
        }),
      );
    } catch (error: unknown) {
      setFailure(error instanceof Error ? error.message : String(error));
      setPending(false);
    }
  };

  return (
    <Dialog
      isOpen
      onOpenChange={open => {
        if (!open) onClose();
      }}
      width="44rem"
      className="waadoo-env-power waadoo-promote"
    >
      <DialogHeader className="waadoo-env-power__header">
        <span className="waadoo-env-power__title">
          <span className="waadoo-env-power__chip" aria-hidden="true">
            <PromotionIcon focusable="false" />
          </span>
          {t('promote.title', { source, target })}
        </span>
      </DialogHeader>
      <DialogBody className="waadoo-env-power__body">
        <Text
          as="p"
          variant="body-small"
          color="secondary"
          className="waadoo-env-power__subtitle"
        >
          {production
            ? t('promote.subtitleAdmins', { project })
            : t('promote.subtitleEditors', { project })}
        </Text>
        <Flex direction="column" gap="3">
          <section aria-labelledby="waadoo-promote-commits">
            <Text
              as="h3"
              id="waadoo-promote-commits"
              variant="body-x-small"
              weight="bold"
              color="secondary"
              className="waadoo-env-power__section"
            >
              {t('promote.commitsTitle', {
                count: pair.ahead,
                target,
              })}
            </Text>
            <ul className="waadoo-promote__commits">
              {pair.commits.map(commit => {
                const at = commit.date ? Date.parse(commit.date) : NaN;
                const meta = [
                  commit.author,
                  Number.isNaN(at) ? undefined : relativeTime(at, now, locale),
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <li key={commit.sha}>
                    <CommitIcon aria-hidden focusable="false" />
                    <code>{commit.shortSha}</code>
                    <span className="waadoo-promote__commit-title">
                      {commit.title}
                    </span>
                    {meta && (
                      <span className="waadoo-promote__commit-meta">
                        {meta}
                      </span>
                    )}
                  </li>
                );
              })}
              {hidden > 0 && (
                <li className="waadoo-promote__more">
                  {t('promote.moreCommits', { more: String(hidden) })}
                </li>
              )}
            </ul>
          </section>
          <div className="waadoo-promote__compare">
            <section className="waadoo-promote__box" data-tone="change">
              <Text as="h3" variant="body-small" weight="bold">
                {t('promote.changesTitle', { target })}
              </Text>
              <ul>
                {standard ? (
                  <li>{t('promote.changeCodeStandard')}</li>
                ) : (
                  <>
                    <li>{t('promote.changeCode')}</li>
                    <li>{t('promote.changeImage', { source, target })}</li>
                  </>
                )}
                <li>{t('promote.changeCommon')}</li>
              </ul>
            </section>
            <section className="waadoo-promote__box" data-tone="kept">
              <Text as="h3" variant="body-small" weight="bold">
                {t('promote.keptTitle')}
              </Text>
              <Kept target={target} config={config} />
            </section>
          </div>
          {pair.buildingJobs.length > 0 && (
            <Alert
              status="warning"
              icon
              title={t('promote.oldCiWarning', {
                target,
                jobs: pair.buildingJobs.join(', '),
              })}
            />
          )}
          {pair.ciFiles.length > 0 && (
            <Alert
              status="warning"
              icon
              title={t('promote.ciWarning', {
                files: pair.ciFiles.join(', '),
              })}
            />
          )}
          <Text variant="body-small" color="secondary">
            {howText()}
          </Text>
          {production && (
            <TextField
              label={t('envPower.confirmLabel', { confirmation: expected })}
              value={typed}
              onChange={setTyped}
              isDisabled={pending}
            />
          )}
          {failure && (
            <Text variant="body-medium" color="danger" role="alert">
              {t('envPower.launchFailed', { message: failure })}
            </Text>
          )}
        </Flex>
      </DialogBody>
      <DialogFooter>
        {pair.openMergeRequestUrl && (
          <ButtonLink
            variant="secondary"
            href={pair.openMergeRequestUrl}
            target="_blank"
            rel="noopener"
          >
            {t('promote.viewMergeRequest')}
          </ButtonLink>
        )}
        <Button variant="secondary" onPress={onClose} isDisabled={pending}>
          {t('envPower.cancel')}
        </Button>
        <Button
          variant="primary"
          iconStart={<PromotionIcon aria-hidden focusable="false" />}
          isPending={pending}
          isDisabled={!confirmed || templateRef === undefined}
          onPress={() => void submit()}
        >
          {t('promote.submit')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
